/**
 * Local Wikipedia-style mechanic definitions for the Lore Mechanics section.
 * Short definition articles — no flavor, humor, or extra lore.
 * Content is authored here — not loaded from the database.
 */

function mechanicArticle(title, text) {
  if (!title) {
    throw new Error('mechanicArticle: title is required');
  }
  if (!text) {
    throw new Error(`mechanicArticle: text is required for ${title}`);
  }
  return {
    title,
    format: 'wiki',
    text: text.trim(),
  };
}

/** @type {Record<string, { title: string, text: string, format: string }>} */
const mechanicsArticles = {
  Raid: mechanicArticle(
    'Raid',
    `**Raid** is an ability that activates when the unit attacks the opponent's **general**. The effect that follows is defined by the individual unit that has Raid.`
  ),

  Recover: mechanicArticle(
    'Recover',
    `**Recover** is an ability that heals your **general** when the unit deals damage. The general is healed for an amount equal to the damage dealt.`
  ),

  'Mend wounds': mechanicArticle(
    'Mend wounds',
    `**Mend wounds** is an ability that fully restores the unit's **health** at the start of its owner's turn.`
  ),

  Penetrate: mechanicArticle(
    'Penetrate',
    `**Penetrate** is an ability that applies leftover attack damage to the opponent's **general** after this unit destroys an enemy unit. If the unit's attack exceeds the destroyed unit's remaining health, the excess damage is dealt to the general.`
  ),

  Blitz: mechanicArticle(
    'Blitz',
    `**Blitz** is an ability that allows a unit to **attack** on the same turn it is deployed. Units without Blitz cannot attack during the turn they enter the battlefield.`
  ),

  'Counter Strike': mechanicArticle(
    'Counter Strike',
    `**Counter Strike** is a property of some **orders**. If an order with Counter Strike is drawn when a **Barrier** is dismantled, it can be issued immediately for **0 Intel**, including during the opponent's turn.`
  ),

  Special: mechanicArticle(
    'Special',
    `**Special** refers to ongoing or passive effects that apply while the unit remains on the battlefield. These effects are distinct from one-time **Deploy** triggers and vary by unit.`
  ),

  Redeem: mechanicArticle(
    'Redeem',
    `**Redeem** is an ability that triggers when the unit is destroyed. The effect that follows is defined by the individual unit that has Redeem. Confused units do not trigger Redeem.`
  ),

  Sniper: mechanicArticle(
    'Sniper',
    `**Sniper** is an ability that deals enough **damage** to destroy the target unit in a single hit. An active **Shield** blocks Sniper: the Shield is removed and the unit takes no health damage. Sniper is not the same as **Destroy**.`
  ),

  Deploy: mechanicArticle(
    'Deploy',
    `**Deploy** is an ability that triggers when a unit is first placed on the battlefield. The effect that follows is defined by the individual unit and may require selecting a **target**.`
  ),

  Protect: mechanicArticle(
    'Protect',
    `**Protect** is an ability that forces enemy units to attack a Protect unit in the same **zone** before they can attack other units or the **general** in that zone. Protect is zone-scoped and prevents the unit from becoming **Out of Reach**.`
  ),

  Shield: mechanicArticle(
    'Shield',
    `**Shield** is an ability that absorbs the next instance of **damage** dealt to the unit. The Shield is then removed, and that hit deals no health damage. Shield can block **Sniper**, but does not prevent **Destroy**.`
  ),

  Fell: mechanicArticle(
    'Fell',
    `**Fell** is an ability that activates when the unit destroys an enemy unit. The effect that follows is defined by the individual unit that has Fell.`
  ),

  Barrier: mechanicArticle(
    'Barrier',
    `A **Barrier** is one of five protective layers on a **general**. Generals begin battles with **30 health** and five Barriers at the health thresholds **25**, **20**, **15**, **10**, and **5**. Barriers cannot be recreated once lost`
  ),

  Dismantle: mechanicArticle(
    'Dismantle',
    `**Dismantle** occurs when a general's **health** crosses a **Barrier** threshold for the first time. The Barrier is removed and that combatant draws an extra **order**. If the drawn order has **Counter Strike**, it can be issued immediately for **0 Intel**. Multiple Barriers can be dismantled by a single hit if several thresholds are crossed.`
  ),

  Immobilize: mechanicArticle(
    'Immobilize',
    `**Immobilize** prevents a unit from attacking on its next turn. The effect ends at the start of the immobilizing combatant's following turn. **Confuse** removes Immobilize.`
  ),

  Confuse: mechanicArticle(
    'Confuse',
    `**Confuse** permanently strips a unit of its native abilities and special effects, leaving it with its base **attack** and **health** characteristics. Equipped **weapons** are removed. Confused units do not trigger native effects such as **Redeem**. External bonuses granted afterward may still apply.`
  ),

  Destroy: mechanicArticle(
    'Destroy',
    `**Destroy** removes a unit from the battlefield immediately without dealing **damage**. **Shield** does not prevent Destroy. This is distinct from reducing a unit's health to zero through damage.`
  ),

  Damage: mechanicArticle(
    'Damage',
    `**Damage** reduces a unit's or general's **health**. An active **Shield** can absorb one instance of damage. When a unit's health reaches zero, it is destroyed through the normal death process.`
  ),

  Grow: mechanicArticle(
    'Grow',
    `**Grow** increases a unit's **attack**, **health**, or both.`
  ),

  Shrink: mechanicArticle(
    'Shrink',
    `**Shrink** decreases a unit's **attack**, **health**, or both. If health is reduced to zero, the unit is destroyed.`
  ),

  Health: mechanicArticle(
    'Health',
    `**Health** is the amount of damage a unit or **general** can sustain. A unit is destroyed when its health reaches zero. Generals begin standard battles with **30** health.`
  ),

  Intel: mechanicArticle(
    'Intel',
    `**Intel** is the resource spent to issued **orders**. Each order has an Intel cost, and available Intel is managed over the course of the battle.`
  ),

  Order: mechanicArticle(
    'Order',
    `An **order** is like a command you can issue during battle and are collected in your **strategy**. Orders include units, Stratagems, Drones, and Weapons. Issuing an order usually costs **Intel**.`
  ),

  Strategy: mechanicArticle(
    'Strategy',
    `A **strategy** is the set of **orders** prepared before a battle. Competitive strategies contain between **50** and **100** orders and cannot be changed during the battle.`
  ),

  'Completed Order': mechanicArticle(
    'Completed Order',
    `A **Completed Order** is an **order** that has already been used, destroyed, or otherwise removed from battle. Completed Orders are normally inactive, though some effects can return them to hand.`
  ),

  Resign: mechanicArticle(
    'Resign',
    `**Resign** allows a a party to forfeit the battle immediately, ending it as a loss for that party.`
  ),

  'End Turn': mechanicArticle(
    'End Turn',
    `**End Turn** ends the current combatant's turn and passes to the **opponent**. If a combatant does not end the turn in time, the game ends it automatically.`
  ),

  Target: mechanicArticle(
    'Target',
    `A **target** is a legal recipient chosen for an order or ability effect, such as a unit, zone, or **general**. Some effects require selecting a target before they resolve.`
  ),

  Front: mechanicArticle(
    'Front',
    `The **Front** is one of the two battlefield **zones**. Units normally attack only within the same zone. The Front is the primary combat zone and does not grant **Out of Reach** by default.`
  ),

  Flank: mechanicArticle(
    'Flank',
    `The **Flank** is one of the two battlefield **zones**. Non-Protect units deployed there become **Out of Reach**, meaning enemy units cannot attack them until that unit attacks. Stratagems and other non-attack effects can still affect them. Units with **Protect** do not become Out of Reach.`
  ),

  Opponent: mechanicArticle(
    'Opponent',
    `The **opponent** is the other combatant in the battle, including their general, units, zones, hand, and other game resources.`
  ),
};

export default mechanicsArticles;
